import React from 'react';
import { ActivityIndicator, Pressable, StyleSheet, View } from 'react-native';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import Logo from '@/components/Icons/Logo';

// Onboarding on every platform uses the neutral palette in docs/BRAND_GUIDELINES.md.
export const onboardingColors = {
  background: '#FFFFFF',
  ink: '#1D1D1F',
  secondary: '#6E6E73',
  surface: '#F5F5F7',
  border: '#DEDEE3',
  selected: '#F0F0F2',
};

export function OnboardingMark({ icon, large = false }) {
  return <View accessible={false} accessibilityElementsHidden importantForAccessibility="no-hide-descendants" style={[styles.mark, large && styles.largeMark]}>
    {icon ? <Icon name={icon} size={large ? 32 : 26} color={onboardingColors.ink} /> : <Logo width={large ? 46 : 32} height={large ? 46 : 32} color={onboardingColors.ink} />}
  </View>;
}

export function NativeOnboardingHeader({ chapter = 0, label, onBack, backLabel = 'Back', disabled, trailing }) {
  return <View style={styles.header}>
    <Pressable onPress={onBack} accessible={!!onBack} accessibilityElementsHidden={!onBack} importantForAccessibility={onBack ? 'auto' : 'no-hide-descendants'} disabled={!onBack || disabled} accessibilityRole="button" accessibilityLabel={backLabel} style={({ pressed }) => [styles.back, { opacity: !onBack ? 0 : pressed ? 0.55 : 1 }]}>
      <Icon name="chevron-left" size={17} color={onboardingColors.ink} />
    </Pressable>
    <View accessible accessibilityLabel={`${label}, step ${chapter + 1} of 3`} style={styles.progress}>
      <Text size="xs" weight="medium" style={{ color: onboardingColors.secondary }}>{label}</Text>
      <View style={styles.track}>{[0, 1, 2].map(index => <View key={index} style={[styles.segment, { backgroundColor: index <= chapter ? onboardingColors.ink : onboardingColors.border }]} />)}</View>
    </View>
    <View style={styles.trailing}>{trailing}</View>
  </View>;
}

export function NativeOnboardingButton({ children, onPress, secondary, disabled, busy, style }) {
  return <Pressable onPress={onPress} disabled={disabled || busy} accessibilityRole="button" accessibilityState={{ disabled: !!(disabled || busy), busy: !!busy }}
    style={({ pressed, hovered }) => [styles.button, secondary && styles.secondaryButton, { opacity: disabled ? 0.4 : pressed ? 0.72 : hovered ? 0.9 : 1 }, style]}>
    {busy && <ActivityIndicator size="small" color={secondary ? onboardingColors.ink : '#FFFFFF'} />}
    <Text size="l" weight="bold" style={{ color: secondary ? onboardingColors.ink : '#FFFFFF', textAlign: 'center', flexShrink: 1 }}>{children}</Text>
  </Pressable>;
}

const styles = StyleSheet.create({
  mark: { width: 64, height: 64, borderRadius: 20, backgroundColor: onboardingColors.surface, alignItems: 'center', justifyContent: 'center' },
  largeMark: { width: 96, height: 96, borderRadius: 28 },
  header: { minHeight: 66, paddingHorizontal: 16, flexDirection: 'row', alignItems: 'center', flexShrink: 0 },
  back: { width: 44, height: 44, alignItems: 'center', justifyContent: 'center', borderRadius: 22, backgroundColor: onboardingColors.surface },
  progress: { flex: 1, alignItems: 'center', gap: 9 },
  track: { flexDirection: 'row', width: 104, gap: 5 },
  segment: { flex: 1, height: 3, borderRadius: 2 },
  trailing: { width: 44, minHeight: 44, alignItems: 'center', justifyContent: 'center' },
  button: { minHeight: 56, paddingHorizontal: 20, paddingVertical: 15, borderRadius: 16, backgroundColor: onboardingColors.ink, flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 10 },
  secondaryButton: { backgroundColor: onboardingColors.surface },
});
